import AddIcon from "@mui/icons-material/AddCircleOutlineRounded";
import { default as DeleteIcon } from "@mui/icons-material/HighlightOffRounded";
import { isEqual } from "lodash";
import { FC, useEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import {
  useGetContractDocumentDropdownPreload,
  useGetHrAndBillingContactsMutation,
  useGetPurchaseOrders,
  useGetStaffListByJobRoleOrClientId,
  useValidateOpportunityIdMutation,
} from "../../../../api/services/client/clientApis";
import {
  GetContractDocumentPayload,
  GetContractDocumentResponse,
  ValidateOpportunityPayload,
} from "../../../../api/services/client/models";
import { useGetPaymentTerms } from "../../../../api/services/program/programApis";
import { useGetLineOfBusiness } from "../../../../api/services/programTemplate/programTemplateApis";
import { useGetUsersDetailByRoleMutation } from "../../../../api/services/user/userApi";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  FormControllerCheckbox,
  MuiBox,
  MuiButtonTertiary,
  MuiCheckbox,
  MuiGrid,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTextField,
  MuiTypography,
  WarningConfirmationModal,
} from "../../../../components";
import { ButtonWithIcon } from "../../../../components/buttonWithIcon/ButtonWithIcon";
import { STATUS_BADGE_TYPE } from "../../../../components/statusBadge/StatusBadge";
import {
  BE_ERROR_CODES,
  EMPTY_VALUE_DASH,
  LANGUAGE_CODE,
  MAX_LENGTH_75,
  MODULE_PERMISSIONS,
  PAGE_IDS,
  ROLE_IDS,
} from "../../../../helpers/constants";
import { UserDetails } from "../../../../helpers/types/common";
import {
  contractIdNameFormat,
  formatApiDataToDropDownOption,
  getParsedBEErrors,
  getValueOrBlank,
  isEmpty,
  isErrorCode,
  isNotEmpty,
  replaceHashWithValue,
  setApiDataToDropDownOption,
} from "../../../../helpers/utils";
import { useForceUpdate } from "../../../../hooks/useForceUpdate";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useToast } from "../../../../hooks/useToast";
import { toggleLoader } from "../../../../redux/slices/appState/appStateSlice";
import { createClientSelector } from "../../../../redux/slices/client/createClientSelector";
import { LabelWithIconText, Separator } from "../../../../styles/common.style";
import {
  CreateClientFormKey as BillingConfigurationFormKey,
  BillingConfigurationItem,
  CreateClientFormKey,
  emptyBillingConfigurationItem,
  PurchaseOrder,
} from "../../modelsAndSchemas/Models";
import {
  billingAndPurchaseOrderKeys,
  createClientSiteCoreKeys,
} from "../../siteCore/SiteCoreKeys";
import BillingProgramTemplate from "../billingProgramTemplate/BillingProgramTemplate";
import {
  BillingConfigurationWrapper,
  TooltipAddIconWrapper,
} from "./BillingConfiguration.style";
import { usePermissions } from "../../../../hooks/usePermissions";

export interface BillingConfigurationProps {
  isNewContactAdded?: boolean;
  setIsNewContactAdded?: (isNewContactAdded: boolean) => void;
  onConfigOrProgramTemplateDeleted?: (isDeleted: boolean) => void;
  triggerAddContactModal?: () => void;
}

export const BillingConfiguration: FC<BillingConfigurationProps> = ({
  isNewContactAdded,
  setIsNewContactAdded,
  onConfigOrProgramTemplateDeleted,
  triggerAddContactModal,
}) => {
  const { gridSizeMedium: gridSize } = useGridContainer(false);
  const pageId = PAGE_IDS.CREATE_CLIENT_BILLING_CONFIGURATION;
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchComponentTitle,
    commonValidationMessages,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { control, getValues, setValue, clearErrors, setError } =
    useFormContext();
  const { id: clientId } = useSelector(createClientSelector);

  const [configurationAccordionStatus, setConfigurationAccordionStatus] =
    useState<boolean[]>([]);
  const [configNameList, setConfigNameList] = useState<string[]>([]);

  const [purchaseOrderOptions, setPurchaseOrderOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState(false);
  const indexRef = useRef<number>();

  const [tooltipMessageList, setTooltipMessageList] = useState<string[]>([]);
  const [showTooltipErrorIconList, setShowTooltipErrorIconList] = useState<
    boolean[]
  >([]);
  const [
    contractDocumentDropdownDataList,
    setContractDocumentDropdownDataList,
  ] = useState<GetContractDocumentResponse[][]>([]);
  const [contractHasOpportunityIdList, setContractHasOpportunityIdList] =
    useState<boolean[]>([]);
  const [contractDropdownParamsList, setContractDropdownParamsList] = useState<
    GetContractDocumentPayload[]
  >([]);
  const [isOpportunityChangeManually, setIsOpportunityChangeManually] =
    useState<boolean>(false);
  const forceUpdate = useForceUpdate();

  const { isMobile } = useMediaQueries();

  const {
    billingDetailsAndPOTitle,
    billingConfigurationsHeading,
    addConfiguration,
    configurationNumber,
    lineOfBusiness,
    billingContactName,
    hrContact,
    outreachEligibility,
    costCentre,
    businessUnit,
    poNumber,
    candidateLocation,
    site,
    projectManager,
    projectAdminCoordinator,
    additionalProjectResource,
    outreachEligibilityDropdown,
    addHrContactMessage,
    addBillingContactMessage,
    deleteConfigurationActionText,
    configurationDeletedMessage,
    salesOffice,
    billingConfigurationName,
    selectPreferencesHeader,
    employeeIDCheckbox,
    employeeBandCheckbox,
    additionalContactForInvoice,
    clientInformation1,
    clientInformation2,
    clientInformation3,
    opportunityId,
    opportunityName,
    contractIdAndName,
    paymentTermsDropDown,
    opportunityIDInvalidMessage,
    opportunityIDAssociatedWithAnotherClient,
    opportunityIDAssociatedWithProgramMessage,
  } = billingAndPurchaseOrderKeys;

  const getLabel = (key: string) => {
    return fetchLabel(billingDetailsAndPOTitle, key);
  };

  const { data: lineOfBusinessData, isLoading: businessPending } =
    useGetLineOfBusiness(LANGUAGE_CODE.EN_US);

  const {
    mutateAsync: getHrContacts,
    data: hrContacts,
    isPending: isHrContactsPending,
  } = useGetHrAndBillingContactsMutation();

  const {
    mutateAsync: getBillingContacts,
    data: billingContacts,
    isPending: isBillingContactsPending,
  } = useGetHrAndBillingContactsMutation();

  const { data: staffListData, isPending: isStaffListPending } =
    useGetStaffListByJobRoleOrClientId({
      clientId,
    });

  const {
    mutateAsync: getProjectManagersByRole,
    data: projectManagersList,
    isPending: isGetProjectManagersByRolePending,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: getProjectCoOrdinatorsByRole,
    data: projectCoOrdinatorsList,
    isPending: isGetProjectCoOrdinatorsByRolePending,
  } = useGetUsersDetailByRoleMutation();

  const {
    data: purchaseOrdersList,
    refetchWithParams: refetchPurchaseOrders,
    isSuccess: isGetPurchaseOrdersSuccess,
  } = useGetPurchaseOrders();

  const {
    mutateAsync: getGetPaymentTerms,
    data: paymentTermsList,
    isPending: isPaymentTermsListPending,
  } = useGetPaymentTerms();

  const {
    data: validateOpportunityIdData,
    mutate: validateOpportunityId,
    isError: isValidateOpportunityIdError,
    error: validateOpportunityIdError,
    isSuccess: isValidateOpportunityIdSuccess,
  } = useValidateOpportunityIdMutation();

  const { queries: contractDocumentDropdownQueries } =
    useGetContractDocumentDropdownPreload(contractDropdownParamsList);

  useEffect(() => {
    if (isEmpty(clientId)) return;
    refetchPurchaseOrders({
      clientCode: clientId,
      statusId: STATUS_BADGE_TYPE.active,
    });
  }, [clientId]);

  useEffect(() => {
    getProjectManagersByRole({ roleIds: [ROLE_IDS.projectManager] });
    getProjectCoOrdinatorsByRole({ roleIds: [ROLE_IDS.projectCoOrdinator] });
    getGetPaymentTerms();
  }, []);

  const getHrAndBillingContacts = async () => {
    await getHrContacts({ clientId, contactType: "hr" });
    await getBillingContacts({ clientId, contactType: "billing" });
    setIsNewContactAdded?.(false);
  };

  const billingConfigs = useWatch({
    control,
    name: BillingConfigurationFormKey.configurations,
  });
  const salesOfficeWatch = useWatch({
    control,
    name: CreateClientFormKey.salesOfficeId,
  });

  useEffect(() => {
    const { salesOfficeName } = getValues();

    if (Array.isArray(billingConfigs)) {
      billingConfigs.forEach(
        (_: BillingConfigurationItem, configIndex: number) => {
          const formIdPrefix = `${BillingConfigurationFormKey.configurations}[${configIndex}]`;

          setValue(
            `${formIdPrefix}.${BillingConfigurationFormKey.salesOfficeId}`,
            salesOfficeName ?? EMPTY_VALUE_DASH,
          );
        },
      );
    }
  }, [salesOfficeWatch, billingConfigs?.length]);

  useEffect(() => {
    if (Array.isArray(billingConfigs) && Array.isArray(purchaseOrdersList)) {
      billingConfigs.forEach(
        (config: BillingConfigurationItem, idx: number) => {
          const hasPoNumber = purchaseOrdersList.some(
            (po: PurchaseOrder) => po.purchaseOrderId === config.poNumberId,
          );
          if (!hasPoNumber && config.poNumberId) {
            setValue(
              `${BillingConfigurationFormKey.configurations}.${idx}.poNumberId`,
              "",
            );
            setValue(
              `${BillingConfigurationFormKey.configurations}.${idx}.poNumber`,
              "",
            );
          }
        },
      );
    }
  }, [billingConfigs, purchaseOrdersList]);

  useEffect(() => {
    setPurchaseOrderOptions([]);

    if (
      isGetPurchaseOrdersSuccess &&
      purchaseOrdersList &&
      isNotEmpty(purchaseOrdersList)
    ) {
      const poNumberOptions = purchaseOrdersList.map(
        (purchaseOrder: PurchaseOrder) => ({
          label: purchaseOrder.poNumber,
          value: purchaseOrder.purchaseOrderId,
        }),
      );
      setPurchaseOrderOptions(poNumberOptions);
    }
  }, [isGetPurchaseOrdersSuccess, purchaseOrdersList]);

  useEffect(() => {
    getHrAndBillingContacts();
  }, [isNewContactAdded]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        billingPending: {
          isVisible:
            businessPending ||
            isBillingContactsPending ||
            isHrContactsPending ||
            isStaffListPending ||
            isGetProjectManagersByRolePending ||
            isGetProjectCoOrdinatorsByRolePending ||
            isPaymentTermsListPending ||
            contractDocumentDropdownQueries.some((q) => q.isPending),

          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      dispatch(
        toggleLoader({
          billingPending: {
            isVisible: false,
          },
        }),
      );
    };
  }, [
    businessPending,
    isBillingContactsPending,
    isHrContactsPending,
    isStaffListPending,
    isGetProjectManagersByRolePending,
    isGetProjectCoOrdinatorsByRolePending,
    contractDocumentDropdownQueries.some((q) => q.isPending),
  ]);

  const handleConfigurationAccordionStatus = (
    index: number,
    expanded: boolean,
  ) => {
    const status = [...configurationAccordionStatus];
    status[index] = expanded;
    setConfigurationAccordionStatus(status);
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: BillingConfigurationFormKey.configurations,
    keyName: "configId",
  });

  useEffect(() => {
    updateConfigLabelName();
    preloadContractDocumentDropdown();
  }, [fields]);

  useEffect(() => {
    let status = [...configurationAccordionStatus];
    fields.forEach((_field, index) => {
      status[index] = index === fields.length - 1;
    });
    setConfigurationAccordionStatus(status);
  }, [fields.length]);

  const handleAddConfiguration = () => {
    append({
      ...emptyBillingConfigurationItem,
      projectManagerId: getValueOrBlank(
        getValues(CreateClientFormKey.dedicatedProjectManager),
      ),
    });
  };

  const handleModalClose = () => setShowDeleteConfirmationModal(false);

  const onDeleteConfigurationClick = (index: number) => {
    indexRef.current = index;
    setShowDeleteConfirmationModal(true);
  };

  const handleDeleteConfiguration = () => {
    if (isNotEmpty(indexRef.current)) {
      remove(indexRef.current);
      handleModalClose();
      displayToast(getLabel(configurationDeletedMessage), "success");
      indexRef.current = undefined;
      onConfigOrProgramTemplateDeleted?.(true);
    }
  };

  const renderAddContactButton = () => {
    return (
      <TooltipAddIconWrapper>
        <MuiButtonTertiary
          id={`${pageId}-add-contact-button`}
          onClick={triggerAddContactModal}
        >
          <AddIcon className="add-icon" />
          {fetchComponentTitle(createClientSiteCoreKeys.addContactTitle)}
        </MuiButtonTertiary>
      </TooltipAddIconWrapper>
    );
  };

  const configurationMenuItems = [
    {
      onClick: handleAddConfiguration,
      dataTestId: `${pageId}-${BillingConfigurationFormKey.addConfiguration}`,
      label: (
        <LabelWithIconText>
          <AddIcon />
          {getLabel(addConfiguration)}
        </LabelWithIconText>
      ),
    },
  ];

  const getFilteredAdditionalContactForInvoice = (configIndex: number) => {
    const selectedBillingContact =
      getValues().configurations[configIndex].billingContactId;
    return billingContacts
      ? billingContacts.filter(
          (contact) => contact.id !== selectedBillingContact,
        )
      : [];
  };

  const updateAdditionalContactForInvoice = (configIndex: number) => {
    const selectedBillingContact =
      getValues().configurations[configIndex].billingContactId;

    const selectedAdditionalInvoiceContactId =
      getValues().configurations[configIndex].additionalInvoiceContactId;
    if (selectedBillingContact === selectedAdditionalInvoiceContactId) {
      setValue(
        `${BillingConfigurationFormKey.configurations}[${configIndex}].${BillingConfigurationFormKey.additionalInvoiceContactId}`,
        "",
      );
    }
  };

  const updateConfigLabelName = () => {
    const configNameList: string[] = [];
    fields.forEach((_field, index) => {
      const formIdPrefix = `${BillingConfigurationFormKey.configurations}[${index}]`;
      const configName = getValues(
        `${formIdPrefix}.${BillingConfigurationFormKey.billingConfigurationName}`,
      );
      if (isNotEmpty(configName)) {
        configNameList.push(configName);
      } else {
        const label = replaceHashWithValue(
          getLabel(configurationNumber),
          `${index + 1}`,
        );
        configNameList.push(label);
      }
    });

    setConfigNameList(configNameList);
  };

  const handleOpportunityIdValidation = (configIndex: number) => {
    indexRef.current = configIndex;
    const formIdPrefix = `${BillingConfigurationFormKey.configurations}[${configIndex}]`;
    const opportunityId = getValues(
      `${formIdPrefix}.${BillingConfigurationFormKey.opportunityId}`,
    );

    const validateOpportunityPayload: ValidateOpportunityPayload = {
      clientId,
      opportunityId: getValueOrBlank(opportunityId),
      contractDocumentId: "",
      programId: "",
      isForDocument: false,
      isForProgram: false,
    };

    if (isNotEmpty(opportunityId)) {
      validateOpportunityId(validateOpportunityPayload);
    }
  };

  const resetOpportunityIdValidation = (configIndex: number) => {
    const formIdPrefix = `${BillingConfigurationFormKey.configurations}[${configIndex}]`;

    updateTooltipState(configIndex, "", false);
    clearErrors(`${formIdPrefix}.${BillingConfigurationFormKey.opportunityId}`);
    setValue(
      `${formIdPrefix}.${BillingConfigurationFormKey.isOpportunityIdValidated}`,
      false,
    );
    setValue(
      `${formIdPrefix}.${BillingConfigurationFormKey.opportunityName}`,
      EMPTY_VALUE_DASH,
    );
  };

  const updateTooltipState = (
    configIndex: number,
    message: string,
    showError: boolean,
  ) => {
    const updatedTooltipMessageList = [...tooltipMessageList];
    const updatedShowTooltipErrorIconList = [...showTooltipErrorIconList];

    updatedTooltipMessageList[configIndex] = message;
    updatedShowTooltipErrorIconList[configIndex] = showError;

    setTooltipMessageList(updatedTooltipMessageList);
    setShowTooltipErrorIconList(updatedShowTooltipErrorIconList);
  };

  useEffect(() => {
    if (isValidateOpportunityIdSuccess && validateOpportunityIdData) {
      const formIdPrefix = `${BillingConfigurationFormKey.configurations}[${indexRef.current}]`;

      setValue(
        `${formIdPrefix}.${BillingConfigurationFormKey.isOpportunityIdValidated}`,
        true,
      );
      clearErrors(
        `${formIdPrefix}.${BillingConfigurationFormKey.opportunityId}`,
      );
      setValue(
        `${formIdPrefix}.${BillingConfigurationFormKey.opportunityName}`,
        getValueOrBlank(validateOpportunityIdData?.name),
      );

      updateTooltipState(indexRef.current!, "", false);
    }
  }, [isValidateOpportunityIdSuccess, validateOpportunityIdData]);

  useEffect(() => {
    const formIdPrefix = `${BillingConfigurationFormKey.configurations}[${indexRef.current}]`;
    if (
      isValidateOpportunityIdError &&
      validateOpportunityIdError &&
      isErrorCode(
        validateOpportunityIdError,
        BE_ERROR_CODES.OPPORTUNITY_ID_INVALID_VALIDATE_API,
      )
    ) {
      setError(`${formIdPrefix}.${BillingConfigurationFormKey.opportunityId}`, {
        type: "custom",
        message: getLabel(opportunityIDInvalidMessage),
      });
      setShowTooltipErrorIconList([]);
    } else if (
      isErrorCode(
        validateOpportunityIdError,
        BE_ERROR_CODES.VALIDATE_API_OPPORTUNITY_ID_ALREADY_MAPPED_TO_CLIENT,
      )
    ) {
      const parsedError = getParsedBEErrors(validateOpportunityIdError);
      const clientName = getValueOrBlank(
        parsedError?.[0].message?.split("|")[1]?.trim(),
      );
      updateTooltipState(indexRef.current!, clientName, true);
      setError(`${formIdPrefix}.${BillingConfigurationFormKey.opportunityId}`, {
        type: "custom",
        message: getLabel(opportunityIDAssociatedWithAnotherClient),
      });
    } else if (
      isErrorCode(
        validateOpportunityIdError,
        BE_ERROR_CODES.VALIDATE_API_OPPORTUNITY_ID_ALREADY_MAPPED_TO_PROGRAM,
      )
    ) {
      const parsedError = getParsedBEErrors(validateOpportunityIdError);
      const programName = getValueOrBlank(
        parsedError?.[0].message?.split("|")[0]?.trim(),
      );
      updateTooltipState(indexRef.current!, programName, true);
      setError(`${formIdPrefix}.${BillingConfigurationFormKey.opportunityId}`, {
        type: "custom",
        message: getLabel(opportunityIDAssociatedWithProgramMessage),
      });
    }
  }, [isValidateOpportunityIdError, validateOpportunityIdError]);

  const preloadContractDocumentDropdown = () => {
    const updatedParamsList = [...contractDropdownParamsList];

    for (let configIndex = 0; configIndex < fields.length; configIndex++) {
      const formIdPrefix = `${BillingConfigurationFormKey.configurations}[${configIndex}]`;

      const lineOfBusinessId = getValues(
        `${formIdPrefix}.${BillingConfigurationFormKey.lineOfBusinessId}`,
      );

      if (lineOfBusinessId && !contractDocumentDropdownDataList[configIndex]) {
        updatedParamsList[configIndex] = {
          clientId,
          productLineId: lineOfBusinessId,
          status: `${STATUS_BADGE_TYPE.active},${STATUS_BADGE_TYPE.autoRenewed}`,
          sortBy: "contractId",
        };
      }
    }

    setContractDropdownParamsList(updatedParamsList);
  };

  const updateOpportunityDataForContract = (
    configIndex: number,
    contractData: GetContractDocumentResponse[],
  ) => {
    const formIdPrefix = `${BillingConfigurationFormKey.configurations}[${configIndex}]`;
    const contractId = getValues(
      `${formIdPrefix}.${BillingConfigurationFormKey.contractDocumentId}`,
    );

    if (!isNotEmpty(contractId)) return;

    const contract = contractData?.find((item) => item.id === contractId);
    const opportunityId = getValueOrBlank(contract?.opportunityId);
    const opportunityName = getValueOrBlank(contract?.opportunityName);
    const hasOpportunityData = isNotEmpty(opportunityId);

    if (!isOpportunityChangeManually) {
      const currentOpportunityId = getValues(
        `${formIdPrefix}.${BillingConfigurationFormKey.opportunityId}`,
      );
      if (currentOpportunityId === opportunityId) {
        setValue(
          `${formIdPrefix}.${BillingConfigurationFormKey.isOpportunityIdValidated}`,
          true,
        );
        updateContractOpportunityStatus(configIndex, true);
      }
    } else {
      updateOpportunityFields(formIdPrefix, {
        opportunityId: hasOpportunityData ? opportunityId : "",
        opportunityName: hasOpportunityData
          ? opportunityName
          : EMPTY_VALUE_DASH,
        isValidated: hasOpportunityData,
      });
      updateContractOpportunityStatus(configIndex, hasOpportunityData);
    }
  };

  const processContractQueries = () => {
    let hasUpdate = false;
    const updatedList = [...contractDocumentDropdownDataList];

    contractDocumentDropdownQueries.forEach((query, index) => {
      if (query.data && !isEqual(updatedList[index], query.data)) {
        updatedList[index] = query.data;
        hasUpdate = true;
        updateOpportunityDataForContract(index, query.data);
      }
    });

    if (hasUpdate) {
      setContractDocumentDropdownDataList(updatedList);
    }
  };

  useEffect(() => {
    processContractQueries();
  }, [
    contractDocumentDropdownQueries.map((q) => q.data), // stable dependency
  ]);

  const onChangeLineOfBusiness = (configIndex: number) => {
    const formIdPrefix = `${BillingConfigurationFormKey.configurations}[${configIndex}]`;
    const lineOfBusinessId = getValues(
      `${formIdPrefix}.${BillingConfigurationFormKey.lineOfBusinessId}`,
    );

    if (!lineOfBusinessId) return;
    const contractDocumentId = getValues(
      `${formIdPrefix}.${BillingConfigurationFormKey.contractDocumentId}`,
    );

    indexRef.current = configIndex;
    const updatedParamsList = [...contractDropdownParamsList];

    updatedParamsList[configIndex] = {
      clientId,
      productLineId: lineOfBusinessId,
      status: `${STATUS_BADGE_TYPE.active},${STATUS_BADGE_TYPE.autoRenewed}`,
      sortBy: "contractId",
    };

    contractDocumentDropdownQueries[configIndex]?.refetch &&
      contractDocumentDropdownQueries[configIndex]?.refetch(); // refetch data

    setContractDropdownParamsList(updatedParamsList);
    if (isNotEmpty(contractDocumentId)) {
      setValue(
        `${formIdPrefix}.${BillingConfigurationFormKey.contractDocumentId}`,
        "",
      );
      onChangeContractId(configIndex);
    }

    forceUpdate();
  };

  const onChangeContractId = (configIndex: number) => {
    const formIdPrefix = `${BillingConfigurationFormKey.configurations}[${configIndex}]`;
    const contractId = getValues(
      `${formIdPrefix}.${BillingConfigurationFormKey.contractDocumentId}`,
    );

    indexRef.current = configIndex;
    setIsOpportunityChangeManually(true);

    if (!contractId || !contractDocumentDropdownDataList[configIndex]) {
      clearContractFields(formIdPrefix);
      updateContractOpportunityStatus(configIndex, false);
      return;
    }

    const contractData = contractDocumentDropdownDataList[configIndex]?.find(
      (item) => item.id === contractId,
    );

    const opportunityId = getValueOrBlank(contractData?.opportunityId);
    const opportunityName = getValueOrBlank(contractData?.opportunityName);
    const paymentTermId = getValueOrBlank(contractData?.paymentTermId);

    const hasOpportunityData = isNotEmpty(opportunityId);

    updateOpportunityFields(formIdPrefix, {
      opportunityId: hasOpportunityData ? opportunityId : "",
      opportunityName: hasOpportunityData ? opportunityName : EMPTY_VALUE_DASH,
      isValidated: hasOpportunityData,
    });

    setValue(
      `${formIdPrefix}.${BillingConfigurationFormKey.paymentTermId}`,
      paymentTermId,
    );

    updateContractOpportunityStatus(configIndex, hasOpportunityData);
    forceUpdate();
  };

  const clearContractFields = (formIdPrefix: string) => {
    setValue(
      `${formIdPrefix}.${BillingConfigurationFormKey.opportunityId}`,
      "",
    );
    setValue(
      `${formIdPrefix}.${BillingConfigurationFormKey.opportunityName}`,
      EMPTY_VALUE_DASH,
    );
    setValue(
      `${formIdPrefix}.${BillingConfigurationFormKey.paymentTermId}`,
      "",
    );
  };

  const updateOpportunityFields = (
    formIdPrefix: string,
    {
      opportunityId,
      opportunityName,
      isValidated,
    }: {
      opportunityId: string;
      opportunityName: string;
      isValidated: boolean;
    },
  ) => {
    setValue(
      `${formIdPrefix}.${BillingConfigurationFormKey.opportunityId}`,
      opportunityId,
    );
    setValue(
      `${formIdPrefix}.${BillingConfigurationFormKey.opportunityName}`,
      opportunityName,
    );
    setValue(
      `${formIdPrefix}.${BillingConfigurationFormKey.isOpportunityIdValidated}`,
      isValidated,
    );
  };

  const updateContractOpportunityStatus = (
    configIndex: number,
    hasOpportunityData: boolean,
  ) => {
    setContractHasOpportunityIdList((prev) => {
      const updated = [...prev];
      updated[configIndex] = hasOpportunityData;
      return updated;
    });
  };

  const setContractDocumentDataToDropDownOption = (
    data: GetContractDocumentResponse[] = [],
  ) => {
    return data.map((item) => ({
      label: contractIdNameFormat(item.contractId, item.name),
      value: item.id,
    }));
  };

  return (
    <BillingConfigurationWrapper>
      <form>
        <MuiBox className="title-container">
          <MuiTypography variant="h2SemiBold">
            {getLabel(billingConfigurationsHeading)}
          </MuiTypography>

          {hasModulePermission(MODULE_PERMISSIONS.ADD_CLIENT_BILLING_CONFIG) &&
            (isMobile ? (
              <MuiOptionMenu menuItems={configurationMenuItems} />
            ) : (
              <MuiButtonTertiary
                id={`${pageId}-${BillingConfigurationFormKey.addConfiguration}`}
                onClick={handleAddConfiguration}
              >
                <AddIcon className="add-icon" />
                {getLabel(addConfiguration)}
              </MuiButtonTertiary>
            ))}
        </MuiBox>

        {fields.map((field, index) => {
          const formIdPrefix = `${BillingConfigurationFormKey.configurations}[${index}]`;

          const additionalContactForInvoiceList: UserDetails[] =
            getFilteredAdditionalContactForInvoice(index);

          return (
            <AccordionHeader
              key={field.configId}
              title={<AccordionHeaderText label={configNameList[index]} />}
              onChange={(_, expanded) =>
                handleConfigurationAccordionStatus(index, expanded)
              }
              isExpanded={configurationAccordionStatus[index]}
              className={"accordion-header"}
            >
              {hasModulePermission(
                MODULE_PERMISSIONS.DELETE_CLIENT_BILLING_CONFIG,
              ) && (
                <MuiBox className="semi-header">
                  <ButtonWithIcon
                    data-testId={`${pageId}-${BillingConfigurationFormKey.deleteConfiguration}`}
                    buttonText={commonLabels.delete}
                    onActionClick={() => onDeleteConfigurationClick(index)}
                    aria-label={commonARIALabel.delete}
                    ActionIcon={DeleteIcon}
                  />
                </MuiBox>
              )}
              <Separator />
              <MuiGrid
                container
                spacing={isMobile ? 0 : 2}
                className="main-container"
              >
                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.billingConfigurationName}`}
                  >
                    <MuiTextField
                      id={`${pageId}-${BillingConfigurationFormKey.billingConfigurationName}-${index}`}
                      label={getLabel(billingConfigurationName)}
                      placeholder={commonLabels.inputPlaceHolder}
                      requiredARIALabel={commonARIALabel.required}
                      maxLength={MAX_LENGTH_75}
                      isMandatory
                      onBlur={updateConfigLabelName}
                      tooltipMessage={
                        commonValidationMessages.fieldSpecialCharactersTooltipMessage
                      }
                      tooltipHeaderTitle={
                        commonValidationMessages.allowedSpecialCharactersTitle
                      }
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.lineOfBusinessId}`}
                  >
                    <MuiSelect
                      id={`${pageId}-${BillingConfigurationFormKey.lineOfBusinessId}-${index}`}
                      label={getLabel(lineOfBusiness)}
                      options={setApiDataToDropDownOption(lineOfBusinessData)}
                      value=""
                      visibleDefaultSelectOption={true}
                      isMandatory
                      showSearch
                      placeholder={commonLabels.selectPlaceHolder}
                      onChange={() => onChangeLineOfBusiness(index)}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>

                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.contractDocumentId}`}
                  >
                    <MuiSelect
                      id={`${pageId}-${BillingConfigurationFormKey.contractDocumentId}-${index}`}
                      label={getLabel(contractIdAndName)}
                      options={setContractDocumentDataToDropDownOption(
                        contractDocumentDropdownDataList[index],
                      )}
                      value=""
                      visibleDefaultSelectOption={true}
                      showSearch
                      placeholder={commonLabels.selectPlaceHolder}
                      onChange={() => onChangeContractId(index)}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize} className="form-field">
                  <MuiBox className="opportunity-id-container">
                    <FormController
                      control={control}
                      name={`${formIdPrefix}.${BillingConfigurationFormKey.opportunityId}`}
                    >
                      <MuiTextField
                        id={`${pageId}-${BillingConfigurationFormKey.opportunityId}-${index}`}
                        label={getLabel(opportunityId)}
                        placeholder={commonLabels.inputPlaceHolder}
                        isErrorInfoIconEnabled={showTooltipErrorIconList[index]}
                        tooltipMessage={tooltipMessageList[index]}
                        tooltipHeaderTitle={commonLabels.details}
                        onInput={() => resetOpportunityIdValidation(index)}
                        requiredARIALabel={commonARIALabel.required}
                        disabled={contractHasOpportunityIdList[index]}
                      />
                    </FormController>
                    {!contractHasOpportunityIdList[index] && (
                      <MuiLink
                        className="validate-link"
                        onClick={() => handleOpportunityIdValidation(index)}
                      >
                        {commonLabels.validate}
                      </MuiLink>
                    )}
                  </MuiBox>
                </MuiGrid>
                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.opportunityName}`}
                  >
                    <MuiTextField
                      id={`${pageId}-${BillingConfigurationFormKey.opportunityName}-${index}`}
                      isMandatory
                      disabled
                      label={getLabel(opportunityName)}
                      placeholder={EMPTY_VALUE_DASH}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.paymentTermId}`}
                  >
                    <MuiSelect
                      id={`${pageId}-${BillingConfigurationFormKey.paymentTermId}`}
                      label={getLabel(paymentTermsDropDown)}
                      options={setApiDataToDropDownOption(paymentTermsList)}
                      isMandatory
                      requiredARIALabel={commonARIALabel.required}
                      placeholder={commonLabels.selectPlaceHolder}
                      showSearch
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.billingContactId}`}
                  >
                    <MuiSelect
                      id={`${pageId}-${BillingConfigurationFormKey.billingContactId}-${index}`}
                      label={getLabel(billingContactName)}
                      visibleDefaultSelectOption={true}
                      placeholder={commonLabels.selectPlaceHolder}
                      isMandatory
                      showSearch
                      options={formatApiDataToDropDownOption(
                        billingContacts,
                        true,
                      )}
                      infoMessage={getLabel(addBillingContactMessage)}
                      isInfoIconVisible={isEmpty(billingContacts)}
                      tooltipChildren={renderAddContactButton()}
                      requiredARIALabel={commonARIALabel.required}
                      onChange={() => updateAdditionalContactForInvoice(index)}
                    />
                  </FormController>
                </MuiGrid>

                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.hrContactId}`}
                  >
                    <MuiSelect
                      id={`${pageId}-${BillingConfigurationFormKey.hrContactId}-${index}`}
                      label={getLabel(hrContact)}
                      visibleDefaultSelectOption={true}
                      placeholder={commonLabels.selectPlaceHolder}
                      isMandatory
                      showSearch
                      options={formatApiDataToDropDownOption(hrContacts, true)}
                      infoMessage={getLabel(addHrContactMessage)}
                      isInfoIconVisible={isEmpty(hrContacts)}
                      tooltipChildren={renderAddContactButton()}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.additionalInvoiceContactId}`}
                  >
                    <MuiSelect
                      id={`${pageId}-${BillingConfigurationFormKey.additionalInvoiceContactId}-${index}`}
                      label={getLabel(additionalContactForInvoice)}
                      visibleDefaultSelectOption={true}
                      placeholder={commonLabels.selectPlaceHolder}
                      showSearch
                      options={formatApiDataToDropDownOption(
                        additionalContactForInvoiceList,
                        true,
                      )}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>

                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.outreachEligibilityId}`}
                  >
                    <MuiSelect
                      id={`${pageId}-${BillingConfigurationFormKey.outreachEligibilityId}-${index}`}
                      label={getLabel(outreachEligibility)}
                      isMandatory
                      visibleDefaultSelectOption={true}
                      placeholder={commonLabels.selectPlaceHolder}
                      options={fetchDropdownValues(
                        billingDetailsAndPOTitle,
                        outreachEligibilityDropdown,
                      )}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.poNumberId}`}
                  >
                    <MuiSelect
                      id={`${pageId}-${BillingConfigurationFormKey.poNumberId}-${index}`}
                      label={getLabel(poNumber)}
                      visibleDefaultSelectOption={true}
                      placeholder={commonLabels.selectPlaceHolder}
                      options={purchaseOrderOptions}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.costCentre}`}
                  >
                    <MuiTextField
                      id={`${pageId}-${BillingConfigurationFormKey.costCentre}-${index}`}
                      label={getLabel(costCentre)}
                      placeholder={commonLabels.inputPlaceHolder}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>

                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.businessUnit}`}
                  >
                    <MuiTextField
                      id={`${pageId}-${BillingConfigurationFormKey.businessUnit}-${index}`}
                      label={getLabel(businessUnit)}
                      placeholder={commonLabels.inputPlaceHolder}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>

                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.site}`}
                  >
                    <MuiTextField
                      id={`${pageId}-${BillingConfigurationFormKey.site}-${index}`}
                      label={getLabel(site)}
                      placeholder={commonLabels.inputPlaceHolder}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.candidateLocation}`}
                  >
                    <MuiTextField
                      id={`${pageId}-${BillingConfigurationFormKey.candidateLocation}-${index}`}
                      label={getLabel(candidateLocation)}
                      placeholder={commonLabels.inputPlaceHolder}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>

                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.salesOfficeId}`}
                  >
                    <MuiTextField
                      disabled
                      id={`${pageId}-${formIdPrefix}.${BillingConfigurationFormKey.salesOfficeId}`}
                      label={getLabel(salesOffice)}
                      placeholder={EMPTY_VALUE_DASH}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>

                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.projectManagerId}`}
                  >
                    <MuiSelect
                      id={`${pageId}-${BillingConfigurationFormKey.projectManagerId}-${index}`}
                      label={getLabel(projectManager)}
                      visibleDefaultSelectOption={true}
                      placeholder={commonLabels.selectPlaceHolder}
                      options={formatApiDataToDropDownOption(
                        projectManagersList,
                      )}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>

                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.projectCoordinatorId}`}
                  >
                    <MuiSelect
                      id={`${pageId}-${BillingConfigurationFormKey.projectCoordinatorId}-${index}`}
                      label={getLabel(projectAdminCoordinator)}
                      visibleDefaultSelectOption={true}
                      placeholder={commonLabels.selectPlaceHolder}
                      options={formatApiDataToDropDownOption(
                        projectCoOrdinatorsList,
                      )}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>

                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.additionalProjectResourceId}`}
                  >
                    <MuiSelect
                      id={`${pageId}-${BillingConfigurationFormKey.additionalProjectResourceId}-${index}`}
                      label={getLabel(additionalProjectResource)}
                      visibleDefaultSelectOption={true}
                      placeholder={commonLabels.selectPlaceHolder}
                      options={formatApiDataToDropDownOption(staffListData)}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.clientInformation1}`}
                  >
                    <MuiTextField
                      id={`${pageId}-${BillingConfigurationFormKey.clientInformation1}-${index}`}
                      label={getLabel(clientInformation1)}
                      placeholder={commonLabels.inputPlaceHolder}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.clientInformation2}`}
                  >
                    <MuiTextField
                      id={`${pageId}-${BillingConfigurationFormKey.clientInformation2}-${index}`}
                      label={getLabel(clientInformation2)}
                      placeholder={commonLabels.inputPlaceHolder}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize} className="form-field">
                  <FormController
                    control={control}
                    name={`${formIdPrefix}.${BillingConfigurationFormKey.clientInformation3}`}
                  >
                    <MuiTextField
                      id={`${pageId}-${BillingConfigurationFormKey.clientInformation3}-${index}`}
                      label={getLabel(clientInformation3)}
                      placeholder={commonLabels.inputPlaceHolder}
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>

                <MuiGrid container size={12}>
                  <MuiGrid size={12}>
                    <MuiTypography
                      className="select-preferences-header"
                      variant="h2Bold"
                      component="h2"
                    >
                      {getLabel(selectPreferencesHeader)}
                    </MuiTypography>
                  </MuiGrid>
                  <MuiGrid size={gridSize} role="listitem">
                    <FormControllerCheckbox
                      control={control}
                      name={`${formIdPrefix}.${BillingConfigurationFormKey.hasEmployeeId}`}
                    >
                      <MuiCheckbox
                        checkboxLabel={getLabel(employeeIDCheckbox)}
                        id={`${pageId}-${BillingConfigurationFormKey.hasEmployeeId}`}
                        data-testid={`${pageId}-${BillingConfigurationFormKey.hasEmployeeId}`}
                      />
                    </FormControllerCheckbox>
                  </MuiGrid>
                  <MuiGrid size={gridSize} role="listitem">
                    <FormControllerCheckbox
                      control={control}
                      name={`${formIdPrefix}.${BillingConfigurationFormKey.hasEmployeeBand}`}
                    >
                      <MuiCheckbox
                        checkboxLabel={getLabel(employeeBandCheckbox)}
                        id={`${pageId}-${BillingConfigurationFormKey.hasEmployeeBand}`}
                      />
                    </FormControllerCheckbox>
                  </MuiGrid>
                </MuiGrid>
              </MuiGrid>

              <BillingProgramTemplate
                configIndex={index}
                onConfigOrProgramTemplateDeleted={
                  onConfigOrProgramTemplateDeleted
                }
                lobId={getValues().configurations[index].lineOfBusinessId}
                forceUpdate={forceUpdate}
              />
            </AccordionHeader>
          );
        })}

        {showDeleteConfirmationModal && (
          <WarningConfirmationModal
            show={showDeleteConfirmationModal}
            modalTitle={commonLabels.confirmation}
            onClose={handleModalClose}
            onPrimaryButtonClick={handleDeleteConfiguration}
            labelContent={getLabel(deleteConfigurationActionText)}
          />
        )}
      </form>
    </BillingConfigurationWrapper>
  );
};
